import React from 'react';
import { useEffect } from 'react';
import {
  PrinterStatusResponse,
  useBridgeApi,
  useDispatch,
  useI18n,
  useLogger,
  usePrinterApi,
  useSelector,
} from '~Hooks';
import PrintInstance from './PrintInstance';
import { PrintInQueue } from '~reducers';
import * as actions from '~actions';
import { formatPrinterStatus } from '~utilities';
import { PRINTERS_WITHOUT_STATUS, PrintRow } from '~Components/PrinterManager';
import { createEpsonImage } from './utils';
import useCheckPrinterImageSupport from '~Hooks/useCheckPrinterImageSupport';
import useCloudPrint from './useCloudPrint';
import useEpsonRetryPrint from './useEpsonRetryPrint';

type PrintData = Array<{
  fontSize?: number;
  alignment?: string;
  qr?: boolean;
  content: string;
  image?: {
    width: number;
    height: number;
    data: string;
  };
}>;

const PrintQueue = () => {
  const logger = useLogger('printer');
  const printerAPi = usePrinterApi();
  const printQueue = useSelector((state) => state.POSPrintQueue);
  const printers = useSelector((state) => state.printers);
  const bridgeApi = useBridgeApi();
  const dispatch = useDispatch();
  const { i18n } = useI18n();
  const { checkImagePrintingSupported } = useCheckPrinterImageSupport();
  const { sendCloudPrint } = useCloudPrint();
  const { sendEpsonDirectPrint, shouldUseEpsonRetry } = useEpsonRetryPrint();

  // Finds next print that hasn't been printed yet and prints it
  const nextPrint = printQueue.find((print) => print.isPrinting && !print.error);

  const getStatusesWithTimeout = async (
    printerId: string,
    timeout = 2000,
  ): Promise<PrinterStatusResponse> => {
    // Create a promise that rejects after the timeout
    const timeoutPromise: Promise<PrinterStatusResponse> = new Promise((_, reject) =>
      setTimeout(() => reject(new Error('Timeout')), timeout),
    );

    return Promise.race([bridgeApi.getPrinterStatus(printerId), timeoutPromise]);
  };

  const printTestPrint = async (nxtPrint: PrintInQueue) => {
    // We need to convert the align to lowercase. Reason we use uppercase is because of the generate receipt package
    const formattedData =
      typeof nxtPrint.data === 'string'
        ? nxtPrint.data
        : nxtPrint.data.map((row) => ({
            ...row,
            alignment: row.alignment?.toLowerCase(),
          }));

    const printData = {
      queueId: nxtPrint.id,
      data: formattedData,
      id: nxtPrint.printer,
      base64Image: nxtPrint.base64Image,
    };

    // These models, status works really badly and for starcloud we don't have status yet
    if (PRINTERS_WITHOUT_STATUS.includes(nxtPrint.printerModel)) {
      logger.info('[TEST PRINT] model without status check' + nxtPrint.printer, {
        statuses: {
          printData: nxtPrint,
        },
      });

      // Cloud printers use the backend mutation even for test prints
      if (nxtPrint.printerModel === 'StarCloudPrinter') {
        await sendCloudPrint(nxtPrint);
        return;
      }

      window.postMessageBridge({
        print: printData,
      });
      return;
    }

    try {
      logger.info('[TEST PRINT] Getting printer status ID:' + nxtPrint.printer, {
        printData: nxtPrint,
      });
      const statuses: PrinterStatusResponse = await getStatusesWithTimeout(nxtPrint.printer);
      logger.info('[TEST PRINT] Test printer status' + nxtPrint.printer, {
        statuses: {
          printData: nxtPrint,
          statuses,
        },
      });

      const formattedStatus = formatPrinterStatus(statuses, i18n);

      if (formattedStatus === i18n('Printer.Status.Online')) {
        logger.info('[TEST PRINT] Printer is online', {
          printData: nxtPrint,
        });
        window.postMessageBridge({
          print: printData,
        });
        return;
      }

      dispatch(
        actions.addToast(
          formatPrinterStatus(statuses, i18n),
          true,
          'test-printer-status-toast' + nxtPrint.id,
          true,
        ),
      );
    } catch (error) {
      if (error instanceof Error && error.message === 'Timeout') {
        logger.warn('[TEST PRINT] Printer status request timed out', { printData: nxtPrint });
        // We are not sure of the status of the printer, so we send the print anyway so the printer sends the error (not via get status)
        window.postMessageBridge({
          print: printData,
        });
      } else {
        logger.warn('[TEST PRINT] Error fetching printer status', {
          errorMessage: error ?? 'Unknown error',
          nextPrint,
        });
        // We are not sure of the status of the printer, so we send the print anyway so the printer sends the error (not via get status)
        window.postMessageBridge({
          print: printData,
        });
      }
    }
  };

  useEffect(() => {
    // Async function to handle image processing
    const processPrint = async () => {
      if (!nextPrint) return;

      // Special handling for FAULTY_TEST printer - simulate print failure for ALL print types
      if (nextPrint.printerModel === 'FAULTY_TEST') {
        // Simulate a delay before failure
        setTimeout(() => {
          dispatch({
            type: actions.MESSAGE_PRINT_BRIDGE_REQUEST_FAILED,
            id: nextPrint.id,
            error: `[SIMULATED ERROR - ${nextPrint.printType}] Printer is offline`,
          });
        }, 500);
        return;
      }

      if (nextPrint.printType === 'TEST_PRINT' && !nextPrint.base64Image) {
        printTestPrint(nextPrint);

        // No need to keep a test print in the queue if it fails
        dispatch(actions.removePrintFromQueue(nextPrint.id));
        return;
      }

      const name = printerAPi.getNameFromId(nextPrint.printer);

      const message = name
        ? `Send print to bridge. Type: ${nextPrint.printType} Printer: ${name}`
        : `Send print to bridge. Type: ${nextPrint.printType} Printer: ${nextPrint.printer}`;

      logger.info(message, {
        id: nextPrint.id,
        printerId: nextPrint.printer,
        data: nextPrint.data,
        printerState: printers,
        base64Image: nextPrint.base64Image,
      });

      // Cloud printers are sent via backend mutation instead of the native bridge
      if (nextPrint.printerModel === 'StarCloudPrinter') {
        logger.info('Routing print to cloud printer', {
          queueId: nextPrint.id,
          printerId: nextPrint.printer,
          printType: nextPrint.printType,
        });
        await sendCloudPrint(nextPrint);
        return;
      }

      // Epson direct prints are sent directly to the printer via HTTP ePOS endpoint
      if (shouldUseEpsonRetry(nextPrint.printerModel)) {
        await sendEpsonDirectPrint(nextPrint);
        return;
      }

      let imageData: {
        data: string;
        width: number;
        height: number;
      } | null = null;

      const imagePrintingSupported = checkImagePrintingSupported(nextPrint.printerModel || '');

      if (!imagePrintingSupported && nextPrint.base64Image) {
        logger.error(
          'Image printing not supported for this printer model, base64 sent printqueue',
          {
            printerModel: nextPrint.printerModel,
            printerId: nextPrint.printer,
            base64Image: nextPrint.base64Image,
            nextPrintData: nextPrint.data,
          },
        );
      }

      if (nextPrint.printerModel.startsWith('TM-') && nextPrint.base64Image) {
        // Transform base64 image to canvas for Epson printers
        logger.info('Preparing image for print if exists', {
          printerModel: nextPrint.printerModel,
        });
        logger.info('Processing image for Epson printer', {
          printerModel: nextPrint.printerModel,
        });

        try {
          const paperWidth = 570;
          const epsonImageData = await createEpsonImage(nextPrint.base64Image, logger, paperWidth);

          imageData = epsonImageData;

          logger.info('Image successfully processed for Epson printer', {
            imageData,
          });
        } catch (error) {
          logger.error('Failed to process image for Epson printer', {
            errorMessage: error instanceof Error ? error.message : String(error),
            printerId: nextPrint.printer,
          });
          // Continue without image if processing fails
        }

        // Star printers with base64 image
      } else if (nextPrint.base64Image) {
        let paperWidth = 570;

        // If one of the smaller models, change width
        if (
          nextPrint.printerModel.toLowerCase().includes('mc_print2') ||
          nextPrint.printerModel.toLowerCase().includes('mpop')
        ) {
          paperWidth = 390;
        }

        imageData = {
          data: nextPrint.base64Image,
          width: paperWidth,
          height: 0, // Not used, but required
        };
      }

      // We need to convert the align to lowercase. Reason we use uppercase is because of the generate receipt package
      const formattedData =
        typeof nextPrint.data === 'string'
          ? nextPrint.data
          : nextPrint.data?.map((row) => ({
              ...row,
              alignment: row.alignment?.toLowerCase(),
            })) || '';

      const printData: {
        id: string;
        qr?: string;
        queueId?: string;
        base64Image?: string;
        data: PrintData | string;
      } = {
        queueId: nextPrint.id,
        data:
          nextPrint.base64Image && typeof formattedData !== 'string'
            ? [
                {
                  content: '',
                  image: imageData!,
                },
                ...formattedData,
              ]
            : nextPrint.base64Image && typeof formattedData === 'string'
            ? [
                {
                  content: '',
                  image: imageData!,
                },
                { content: formattedData },
              ]
            : formattedData,
        id: nextPrint.printer,
      };

      logger.info('Post message bridge print data', {
        printData,
        nextPrint,
      });

      window.postMessageBridge({
        print: printData,
      });
    };

    // Execute the async function
    processPrint();
  }, [nextPrint]);

  return (
    <>
      {printQueue.map((p) => (
        <PrintInstance printId={p.id} key={p.id} />
      ))}
    </>
  );
};

export default PrintQueue;
